import { useEffect, useState } from 'react'
import RecordPage, { AboutCard, RecordTimeline, AssociationsColumn } from './RecordPage.jsx'
import ActivityComposer from './ActivityComposer.jsx'
import { buildGroups } from './recordAssociations.js'
import { tsToDate, fmtDate, fmtMoney, objToken, engagementType, engagementTypeLabel } from '../lib/format.js'
import DriveLinksField from './DriveLinksField.jsx'
import EmailPanel from './EmailPanel.jsx'
import SyncChip from './SyncChip.jsx'
import { calendarSyncState } from '../lib/google.js'
import { Field, Card, Chip, Button, Modal, SlideOver, StackColumns, TagLabel } from '../ds/index.js'
import { Trash } from '@phosphor-icons/react'

/**
 * Engagement detail on the three-column RecordPage shell.
 *
 * Wave 2: the property editors that used to be hand-rolled here are now driven by
 * `propertyDefs` through <AboutCard>, exactly like DealRecord/ContactRecord. That
 * is what closes register item 14 ("revenue instead of invoices" — `fee` is simply
 * labelled Revenue in the defs) and item 15 (the header meta line), and it is why
 * this file is roughly a third of its former size.
 *
 * Deliberately NOT moved onto propertyDefs, because none of them is a scalar
 * property: the stage chip row, action items, notes, named links, Drive links, the
 * calendar SyncChip, and the cascade-delete confirm dialog.
 *
 * variant="panel" (the default from the boards) renders the same pieces in a
 * right SlideOver over the board — like a task in a project — instead of a page:
 * title, meta and stage chips pinned at the top, then Overview (action items,
 * notes, activity) / Details (About, calendar, links) / Related (email,
 * contacts, companies) tabs. `onBack` closes it.
 */
export const ENGAGEMENT_PANEL_WIDTH = 640

export default function EngagementRecord({
  engagement: e, defs = [], stages = [], users = [], usersById = {}, linkedTasks = [], statusesById = {},
  associations = [], activities = [], recordsByToken = {}, candidatesByType = {},
  onUpdate, onChangeField, onDelete, onAddActionItem, onToggleTask, onDeleteTask, onUnlinkTask,
  onLink, onUnlink, onAddActivity, onOpenToken, onBack, onSyncCalendar,
  emailTemplates = [], onLogEmail, sender = null, variant = 'page',
}) {
  const [notes, setNotes] = useState(e.notes || '')
  const [links, setLinks] = useState(e.links || [])
  const [itemDraft, setItemDraft] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [killSet, setKillSet] = useState({}) // taskId -> true (will delete)
  const submitItem = () => {
    const t = itemDraft.trim()
    if (t) { onAddActionItem(e, t); setItemDraft('') }
  }

  useEffect(() => { setNotes(e.notes || ''); setLinks(e.links || []) }, [e.id])

  const saveLinks = (next) => { setLinks(next); onUpdate(e.id, { links: next }) }

  const openConfirm = () => {
    setKillSet(Object.fromEntries(linkedTasks.map((t) => [t.id, true])))
    setConfirmDelete(true)
  }
  const runDelete = async () => {
    for (const t of linkedTasks) {
      if (killSet[t.id]) await onDeleteTask(t.id)
      else await onUnlinkTask(t.id)
    }
    onDelete(e.id)
  }

  const groups = buildGroups(objToken('engagement', e.id), associations, recordsByToken, { onOpenToken, candidatesByType, onLink, onUnlink }, [
    { title: 'Contacts', type: 'contact', label: (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—', sub: (r) => r.email || '' },
    { title: 'Companies', type: 'company', label: (r) => r.name || '—', sub: (r) => r.domain || '' },
  ])

  const stageChips = (
    <div style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
      {stages.map((s) => (
        <Chip key={s.id} as="button" active={s.id === e.stage} dot color={s.color} onClick={() => onUpdate(e.id, { stage: s.id })}>
          {s.name}
        </Chip>
      ))}
    </div>
  )

  // ── Details: the defs-driven About card + calendar + links (the page's left
  //    column also leads with the stage chips; the panel pins those on top) ──
  const details = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AboutCard defs={defs} record={e} users={users} onChange={onChangeField} />

      <Card>
        <div className="eyebrow" style={{ margin: '0 0 8px' }}>
          <span className="tag-label" style={{ color: 'var(--color-gold)' }}>Calendar</span>
        </div>
        <SyncChip state={calendarSyncState(e)} onSync={() => onSyncCalendar && onSyncCalendar(e.id)} />
      </Card>

      <Card>
        <div className="eyebrow" style={{ margin: '0 0 8px' }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Links</span></div>
        <div style={{ display: 'grid', gap: 8 }}>
          {links.map((lnk, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input value={lnk.label} onChange={(ev) => setLinks(links.map((x, j) => j === i ? { ...x, label: ev.target.value } : x))} onBlur={() => saveLinks(links)} placeholder="Label" className="cn-input" style={{ width: 120 }} />
              <input value={lnk.url} onChange={(ev) => setLinks(links.map((x, j) => j === i ? { ...x, url: ev.target.value } : x))} onBlur={() => saveLinks(links)} placeholder="https://…" className="cn-input" />
              <button onClick={() => saveLinks(links.filter((_, j) => j !== i))} title="Remove" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 13 }}>✕</button>
            </div>
          ))}
          <button onClick={() => setLinks([...links, { label: '', url: '' }])} style={{ background: 'none', border: '1px dashed var(--border-dash)', color: 'var(--color-muted)', borderRadius: 'var(--radius-sm)', fontSize: 12, padding: '7px 10px', cursor: 'pointer', justifySelf: 'start' }}>+ Add link</button>
        </div>
        <div style={{ marginTop: 14 }}>
          <DriveLinksField links={e.driveLinks || []} onSave={(next) => onUpdate(e.id, { driveLinks: next })} />
        </div>
      </Card>
    </div>
  )

  const left = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>{stageChips}</Card>
      {details}
    </div>
  )

  // ── Center overview: action items (linked tasks) + notes ──
  const overview = (
    <div>
      <div className="eyebrow" style={{ margin: '0 0 8px' }}>
        <span className="tag-label" style={{ color: 'var(--color-gold)' }}>Action items</span>
        <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)', marginLeft: 9 }}>{linkedTasks.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {linkedTasks.map((t) => {
          const done = !!statusesById[t.status]?.done
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 9px', background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)' }}>
              <button onClick={() => onToggleTask(t)} title={done ? 'Mark not done' : 'Mark done'} style={{ width: 16, height: 16, borderRadius: 4, border: '1.5px solid ' + (done ? 'var(--color-teal)' : 'var(--border-dash)'), background: done ? 'var(--color-teal)' : 'transparent', color: 'var(--color-bg-dark)', cursor: 'pointer', flexShrink: 0, fontSize: 11, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{done ? '✓' : ''}</button>
              <span style={{ flex: 1, fontSize: 12.5, color: done ? 'var(--color-muted)' : 'var(--color-white)', textDecoration: done ? 'line-through' : 'none' }}>{t.title}</span>
              <button onClick={() => onDeleteTask(t.id)} title="Delete action item" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 13 }}>✕</button>
            </div>
          )
        })}
        <Field value={itemDraft} onChange={(ev) => setItemDraft(ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Enter') submitItem() }} placeholder="+ Add action item — Enter to add" style={{ fontSize: 12.5, padding: '8px 10px' }} />
      </div>

      {/* notes */}
      <div className="eyebrow" style={{ margin: '20px 0 8px' }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Notes</span></div>
      <Field as="textarea" value={notes} onChange={(ev) => setNotes(ev.target.value)} onBlur={() => { if (notes !== (e.notes || '')) onUpdate(e.id, { notes }) }} placeholder="Add notes…" rows={4} style={{ fontSize: 13, lineHeight: 1.6, resize: 'vertical' }} />
    </div>
  )

  const activity = <RecordTimeline activities={activities} usersById={usersById} composer={<ActivityComposer onAdd={onAddActivity} />} />

  // Register item 10: compose straight from the engagement. Recipients come from
  // the engagement's linked contacts — which is exactly what item 16's
  // association work made possible.
  const linkedContacts = groups
    .find((g) => g.title === 'Contacts')?.items
    .map((it) => recordsByToken[it.token])
    .filter((c) => c && c.email) || []

  const right = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <EmailPanel
          recipients={linkedContacts}
          templates={emailTemplates}
          engagement={e}
          sender={sender}
          onLogEmail={onLogEmail}
          emptyHint="Link a contact with an email address to this engagement to compose from here."
        />
      </Card>
      <AssociationsColumn groups={groups} />
    </div>
  )

  // ── Top-bar delete button ──
  const actions = (
    <button onClick={openConfirm} title="Delete engagement" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', width: 28, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Trash size={16} />
    </button>
  )

  // Register item 15: title, then client / date / revenue on one line.
  const meta = [e.org, fmtDate(tsToDate(e.date)), fmtMoney(e.fee)]

  const deleteDialog = confirmDelete && (
    <Modal
      onClose={() => setConfirmDelete(false)}
      title="Delete engagement?"
      footer={
        <>
          <Button variant="ghost" onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button variant="danger" onClick={runDelete}>Delete</Button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5, margin: '0 0 14px' }}>Deleting <strong style={{ color: 'var(--color-white)' }}>{e.title || 'this engagement'}</strong>. Checked action items are deleted too; unchecked ones are kept and unlinked.</p>
      {linkedTasks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
          {linkedTasks.map((t) => (
            <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--color-white)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!killSet[t.id]} onChange={(ev) => setKillSet((s) => ({ ...s, [t.id]: ev.target.checked }))} />
              <span>{t.title}</span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  )

  if (variant === 'panel') {
    const metaParts = meta.filter(Boolean)
    return (
      <SlideOver
        onClose={onBack}
        title={e.title || 'Engagement'}
        chip={<TagLabel>{engagementTypeLabel(engagementType(e))}</TagLabel>}
        width={ENGAGEMENT_PANEL_WIDTH}
        className="cn-engagement-panel"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={openConfirm}>Delete engagement</Button>
          </div>
        }
      >
        {metaParts.length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12 }}>{metaParts.join(' · ')}</div>
        )}
        <div style={{ marginBottom: 16 }}>{stageChips}</div>
        <StackColumns
          tabbed
          labels={['Overview', 'Details', 'Related']}
          left={<div>{overview}<div style={{ marginTop: 24 }}>{activity}</div></div>}
          center={details}
          right={right}
        />
        {/* Rendered inside the panel's DOM, so a click in the dialog is not an
            "outside" click that would close the panel. */}
        {deleteDialog}
      </SlideOver>
    )
  }

  return (
    <>
      <RecordPage
        objectChip={engagementTypeLabel(engagementType(e))}
        title={e.title || 'Engagement'}
        meta={meta}
        onBack={onBack}
        actions={actions}
        left={left}
        center={{ overview, activity }}
        right={right}
      />
      {deleteDialog}
    </>
  )
}
